import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImage } from "@/lib/api";

export const ImageCarousel = ({ images = [], captions = [], title = "" }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);
  const [count, setCount] = useState(images.length);

  const scrollPrev = useCallback((e) => { e?.stopPropagation(); emblaApi && emblaApi.scrollPrev(); }, [emblaApi]);
  const scrollNext = useCallback((e) => { e?.stopPropagation(); emblaApi && emblaApi.scrollNext(); }, [emblaApi]);
  const scrollTo = useCallback((i, e) => { e?.stopPropagation(); emblaApi && emblaApi.scrollTo(i); }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setCount(emblaApi.scrollSnapList().length);
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  if (!images.length) return <div className="w-full h-72 bg-navy-light" />;

  const caption = captions[selected];

  return (
    <div className="relative group/carousel" data-testid="image-carousel">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {images.map((img, i) => (
            <div key={i} className="flex-[0_0_100%] min-w-0">
              <img src={resolveImage(img)} alt={`${title} ${i + 1}`} className="w-full h-72 sm:h-96 object-cover" draggable={false} />
            </div>
          ))}
        </div>
      </div>

      {count > 1 && (
        <>
          <button onClick={scrollPrev} data-testid="carousel-prev" aria-label="Previous image"
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-gold text-navy p-2 shadow transition-colors">
            <ChevronLeft size={20} />
          </button>
          <button onClick={scrollNext} data-testid="carousel-next" aria-label="Next image"
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-gold text-navy p-2 shadow transition-colors">
            <ChevronRight size={20} />
          </button>
          <div className="absolute top-3 right-3 bg-navy/80 text-white text-xs font-semibold px-2.5 py-1">
            {selected + 1} / {count}
          </div>
        </>
      )}

      {/* Caption + dots overlay */}
      {(caption || count > 1) && (
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-navy/90 to-transparent pt-10 pb-3 px-4">
          {caption && (
            <p data-testid="carousel-caption" className="text-white text-sm font-semibold text-center mb-2 drop-shadow">
              {caption}
            </p>
          )}
          {count > 1 && (
            <div className="flex justify-center gap-2">
              {images.map((_, i) => (
                <button key={i} onClick={(e) => scrollTo(i, e)} data-testid={`carousel-dot-${i}`} aria-label={`Go to image ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${selected === i ? "w-6 bg-gold" : "w-2 bg-white/70 hover:bg-white"}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
